import {
  BASE_PX_PER_MM_VALUE,
  getCalibration,
  resetCalibration,
  setCalibration,
} from "../calibration";
import { getShowSecondaryUnits, getUnits } from "../preferences";
import { parseLengthInput } from "./lengthParse";

const MM_PER_INCH = 25.4;
/** Fraction of the modal's content width the reference line should occupy.
 *  Floored to a round unit count so the actual line is slightly narrower. */
const TARGET_LINE_FRACTION = 0.75;

/** Parse the user-typed length into millimetres. Accepts the same grammar
 *  as `parseLengthInput` in `./lengthParse.ts` — plain numbers, simple /
 *  mixed fractions, and explicit unit suffixes (mm / cm / m / in / inch /
 *  inches / `"` / `''`). The suffix wins over the field's `defaultUnit`
 *  so the user can type "2 7/8\"" into the mm field and have it convert
 *  to 73.025 mm.
 *
 *  The calibration dialog keeps its own parser (rather than reusing the
 *  one in `./lengthParse.ts`) because it accepts only strictly-positive
 *  values — the reference line can't measure a negative length. */
function parseLength(input: string, defaultUnit: "mm" | "in"): number | null {
  const mm = parseLengthInput(input, defaultUnit);
  if (mm === null || !isFinite(mm) || mm <= 0) return null;
  // `parseLengthInput` returns the value in `defaultUnit`; the rest of
  // the dialog reasons in millimetres, so convert when the field is in.
  return defaultUnit === "in" ? mm * MM_PER_INCH : mm;
}

/** Format a value cleanly — strips trailing zeros so 80.000 → "80" but
 *  keeps real fractional digits like 2.875. */
function formatMm(mm: number): string {
  return parseFloat(mm.toFixed(2)).toString();
}
function formatIn(mm: number): string {
  return parseFloat((mm / MM_PER_INCH).toFixed(3)).toString();
}

export class CalibrationDialog {
  private host: HTMLElement;

  constructor(host: HTMLElement) {
    this.host = host;
  }

  open() {
    this.host.classList.add("open");
    this.host.innerHTML = "";

    const units = getUnits();

    const modal = document.createElement("div");
    modal.className = "modal calibration-modal";
    modal.innerHTML = `
      <header>Calibrate screen scale</header>
      <div class="body">
        <p class="calibration-instructions">
          Hold a ruler against the line below and enter its measured length.
          The app will scale every "1:1" rendering so future measurements
          match your screen exactly.
        </p>
        <div class="calibration-line-wrap">
          <div class="calibration-line"></div>
        </div>
        <div class="calibration-input-row">
          <label>Measured length:</label>
          <div class="mm-in-row calibration-mm-in"></div>
        </div>
      </div>
      <footer>
        <button class="btn calibration-reset" data-act="reset">Reset to default</button>
        <span style="flex:1"></span>
        <button class="btn" data-act="cancel">Cancel</button>
        <button class="btn calibration-save" data-act="save">Save</button>
      </footer>
    `;
    // Append now so we can measure the body's available width before sizing
    // the reference line. The line is intentionally drawn at the unmodified
    // CSS-spec DPI (BASE_PX_PER_MM_VALUE — no calibration multiplier) so the
    // pixel width is identical every time the dialog opens; that lets the
    // input field show the user's last-entered value verbatim instead of a
    // value that drifts with the multiplier.
    this.host.appendChild(modal);
    const lineWrap = modal.querySelector(
      ".calibration-line-wrap",
    ) as HTMLElement;
    const availablePx = lineWrap.clientWidth || 480;
    const targetPx = availablePx * TARGET_LINE_FRACTION;
    const targetMm = targetPx / BASE_PX_PER_MM_VALUE;
    // Floor to a round count of the user's active unit. 10 mm and 1 in are
    // the smallest steps that read as "round" on a ruler; both stay >= one
    // step so a tiny dialog still gets a usable reference.
    let referenceMm: number;
    if (units === "in") {
      const roundedIn = Math.max(1, Math.floor(targetMm / MM_PER_INCH));
      referenceMm = roundedIn * MM_PER_INCH;
    } else {
      const roundedMm = Math.max(10, Math.floor(targetMm / 10) * 10);
      referenceMm = roundedMm;
    }
    const linePx = referenceMm * BASE_PX_PER_MM_VALUE;
    (modal.querySelector(".calibration-line") as HTMLElement).style.width =
      `${linePx}px`;

    // Pre-fill: what the user would type for a fresh calibration to land on
    // the current multiplier. Since the line size is fixed at base DPI, this
    // equals what the user actually typed last time (in the same unit). When
    // calibration is at its default (multiplier 1) we have no prior value to
    // display — leave the fields blank instead of seeding a "default" the
    // user might mistake for a real measurement.
    const isDefaultCalibration = getCalibration() === 1;
    const initialMm = isDefaultCalibration
      ? null
      : referenceMm / getCalibration();

    // Build the two paired text inputs. Each accepts plain numbers or values
    // with a unit suffix; the field's labelled unit acts as the default when
    // no suffix is present, but an explicit suffix wins (so "2 7/8\"" in the
    // mm field still parses as inches, then syncs to the in field).
    let currentMm: number | null = initialMm;
    const mmInput = document.createElement("input");
    mmInput.type = "text";
    mmInput.autocomplete = "off";
    mmInput.spellcheck = false;
    mmInput.placeholder = "100";
    mmInput.value = initialMm === null ? "" : formatMm(initialMm);

    const inInput = document.createElement("input");
    inInput.type = "text";
    inInput.autocomplete = "off";
    inInput.spellcheck = false;
    inInput.placeholder = "4 or 2 7/8";
    inInput.value = initialMm === null ? "" : formatIn(initialMm);

    const sync = (sourceValue: string, defaultUnit: "mm" | "in"): boolean => {
      const mm = parseLength(sourceValue, defaultUnit);
      if (mm === null) return false;
      currentMm = mm;
      // Refresh the *other* field to mirror; leave the source field alone so
      // the user's literal text (e.g. "2 7/8\"") stays editable.
      if (defaultUnit === "mm") inInput.value = formatIn(mm);
      else mmInput.value = formatMm(mm);
      return true;
    };
    const onMmChange = () => {
      // Empty input is allowed — clears the paired field and leaves currentMm
      // unset so Save can short-circuit to the "enter a length" alert.
      if (mmInput.value.trim() === "") {
        currentMm = null;
        inInput.value = "";
        return;
      }
      if (!sync(mmInput.value, "mm")) {
        // Invalid — revert to the last good value (or empty if none).
        mmInput.value = currentMm === null ? "" : formatMm(currentMm);
      }
    };
    const onInChange = () => {
      if (inInput.value.trim() === "") {
        currentMm = null;
        mmInput.value = "";
        return;
      }
      if (!sync(inInput.value, "in")) {
        inInput.value = currentMm === null ? "" : formatIn(currentMm);
      }
    };
    mmInput.addEventListener("change", onMmChange);
    inInput.addEventListener("change", onInChange);

    const row = modal.querySelector(".calibration-mm-in") as HTMLElement;
    const cells = [makeUnitCell(mmInput, "mm"), makeUnitCell(inInput, "in")];
    // Preferred unit appears first so the user's primary input lines up
    // visually with the rest of the app's mm/in pairs. When the Settings
    // "Show secondary units" toggle is off, only the primary cell mounts —
    // matching how the properties-panel length fields behave.
    if (units === "in") cells.reverse();
    const visibleCells = getShowSecondaryUnits() ? cells : [cells[0]!];
    for (const c of visibleCells) row.appendChild(c);

    modal.addEventListener("click", (ev) => {
      const btn = (ev.target as HTMLElement).closest(
        "[data-act]",
      ) as HTMLButtonElement | null;
      if (!btn) return;
      const act = btn.dataset.act;
      if (act === "cancel") {
        this.close();
      } else if (act === "reset") {
        resetCalibration();
        this.close();
      } else if (act === "save") {
        // If the focused field has unsubmitted text, parse it now so the user
        // doesn't need to tab/blur before clicking Save.
        const active = document.activeElement;
        if (active === mmInput) onMmChange();
        else if (active === inInput) onInChange();
        if (currentMm === null || !isFinite(currentMm) || currentMm <= 0) {
          alert(
            'Couldn\'t parse that length. Try "100", "2.875", "2 7/8\\"", or "4 in".',
          );
          return;
        }
        // Direct formula: the line is drawn at base DPI (no calibration
        // applied), so the new multiplier maps the rendered pixel width
        // straight to the measured physical mm. M = REF / measured.
        const next = referenceMm / currentMm;
        setCalibration(next);
        this.close();
      }
    });

    // Submit on Enter for keyboard users.
    const submitOnEnter = (ev: KeyboardEvent) => {
      if (ev.key === "Enter") {
        ev.preventDefault();
        (
          modal.querySelector(".calibration-save") as HTMLButtonElement
        )?.click();
      }
    };
    mmInput.addEventListener("keydown", submitOnEnter);
    inInput.addEventListener("keydown", submitOnEnter);

    this.host.addEventListener("click", this.handleBackdrop);
    document.addEventListener("keydown", this.handleEscape);
    // Focus the preferred-unit field so the user can type immediately.
    const focusInput = units === "in" ? inInput : mmInput;
    focusInput.focus();
    focusInput.select();
  }

  close() {
    this.host.classList.remove("open");
    this.host.innerHTML = "";
    this.host.removeEventListener("click", this.handleBackdrop);
    document.removeEventListener("keydown", this.handleEscape);
  }

  private handleBackdrop = (ev: Event) => {
    if (ev.target === this.host) this.close();
  };

  private handleEscape = (ev: KeyboardEvent) => {
    if (ev.key === "Escape") this.close();
  };
}

function makeUnitCell(input: HTMLInputElement, unit: string): HTMLElement {
  const cell = document.createElement("div");
  cell.className = "mm-in-cell";
  cell.appendChild(input);
  const u = document.createElement("span");
  u.className = "mm-in-unit";
  u.textContent = unit;
  cell.appendChild(u);
  return cell;
}
